/**
 * Add-activity launcher for the asset detail / insights pages.
 *
 * Drops a single "Add Activity" button next to an existing asset's
 * activity list. Click → opens a modal with three slide-views:
 *   1. Category picker (filtered by the asset's type)
 *   2. Activity form (`ActivityFields`)
 *   3. Evidence step (only when an `uploadSession` is provided)
 *
 * Persists each activity via the supplied `onPersistActivity` callback
 * — the call site owns the API mutation and any cache invalidation.
 *
 * Used by the per-asset dashboard and `AssetDetailSection`
 * (insights). Replaces the admin branch that previously lived inside
 * `AssetActivitiesStep` and required a fake `FormData` adapter.
 */

import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import {
  AssetType,
  ActivityUnit,
  FuelType,
  type ActivityTypeWithUnits,
  getValidUnits,
  type EmissionAsset,
} from '../../types/emissions';
import {
  isActivityFormComplete,
  type AssetActivityEntry,
} from './types';
import { ActivityFields } from './ActivityFields';
import { EvidenceStep } from './EvidenceStep';
import {
  ASSET_CATEGORIES,
  OTHER_CATEGORIES,
  createEmptyActivity,
  type SuggestedActivity,
} from './AssetActivitiesStep';
import { ActivityCategoryPicker } from './multi-add/ActivityCategoryPicker';
import { useGetSettingsQuery } from '../../api/emissions';
import type { UseUploadSessionResult } from '../../hooks/useUploadSession';

interface AddActivityToExistingAssetButtonProps {
  asset: EmissionAsset;
  /**
   * Persist the activity via the call site's API mutation. Must return
   * the new activity's id so the modal can chain into the evidence
   * step for that activity.
   */
  onPersistActivity: (activity: AssetActivityEntry) => Promise<{ activityId: string }>;
  /** When provided, an evidence-attach view runs after a successful save. */
  uploadSession?: UseUploadSessionResult;
  /** Required when `uploadSession` is provided — for the evidence-step API calls. */
  initiativeId?: string;
}

interface ModalFormState {
  activity: AssetActivityEntry | null;
  label: string;
  lockedFields: Set<string>;
}

const EMPTY_FORM: ModalFormState = { activity: null, label: '', lockedFields: new Set() };

export const AddActivityToExistingAssetButton = ({
  asset,
  onPersistActivity,
  uploadSession,
  initiativeId,
}: AddActivityToExistingAssetButtonProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [view, setView] = useState<'picker' | 'form' | 'evidence'>('picker');
  const [form, setForm] = useState<ModalFormState>(EMPTY_FORM);
  const [createdActivityId, setCreatedActivityId] = useState<string | null>(null);
  const [isPersisting, setIsPersisting] = useState(false);

  // Org-level default region — falls back when the asset has no region set.
  const { data: settings } = useGetSettingsQuery({ initiativeId: asset.initiativeId });

  const categories = ASSET_CATEGORIES[asset.assetType] ?? OTHER_CATEGORIES;

  const closeModal = () => {
    setModalOpen(false);
    setView('picker');
    setForm(EMPTY_FORM);
    setCreatedActivityId(null);
  };

  const openForm = (suggested: SuggestedActivity) => {
    const activity = createEmptyActivity({
      sourceLabel: suggested.label,
      scope: suggested.scope,
      activityType: suggested.activityType,
      region: asset.region || settings?.defaultRegion || '',
      periodStart: '',
      periodEnd: '',
      name: asset.name ? `${asset.name} — ${suggested.label}` : suggested.label,
    });
    const locked = new Set<string>();
    if (suggested.defaults) {
      Object.assign(activity, suggested.defaults);
      for (const key of Object.keys(suggested.defaults)) {
        locked.add(key);
      }
    }
    // Vehicle assets: lock vehicleType to the asset's own value.
    if (asset.assetType === AssetType.Vehicle && asset.vehicleType) {
      activity.vehicleType = asset.vehicleType;
      locked.add('vehicleType');
    }
    setForm({ activity, label: suggested.label, lockedFields: locked });
    setView('form');
  };

  const updateForm = (updates: Partial<AssetActivityEntry>) => {
    if (!form.activity) {
      return;
    }
    const merged = { ...form.activity, ...updates };
    if ('fuelType' in updates && updates.fuelType !== form.activity.fuelType) {
      const validUnits = getValidUnits(
        form.activity.activityType as ActivityTypeWithUnits,
        (updates.fuelType as FuelType) || undefined,
      );
      if (validUnits.length > 0 && form.activity.unit && !validUnits.includes(form.activity.unit as ActivityUnit)) {
        merged.unit = '' as ActivityUnit;
      }
    }
    setForm((prev) => ({ ...prev, activity: merged }));
  };

  const handleSave = async () => {
    if (!form.activity) {
      return;
    }
    setIsPersisting(true);
    try {
      const { activityId } = await onPersistActivity(form.activity);
      if (uploadSession && initiativeId) {
        setCreatedActivityId(activityId);
        setView('evidence');
      } else {
        closeModal();
      }
    } finally {
      setIsPersisting(false);
    }
  };

  const canSave = form.activity ? isActivityFormComplete(form.activity) : false;

  return (
    <>
      <Button variant='default' onClick={() => { setModalOpen(true); setView('picker'); setForm(EMPTY_FORM); }}>
        <Plus className='tw:size-3.5 tw:mr-1' />
        Add Activity
      </Button>

      <Modal isOpen={modalOpen} toggle={closeModal} centered size='xl' scrollable>
        <ModalHeader toggle={closeModal}>
          {view === 'form'
            ? `Add — ${form.label}`
            : view === 'evidence'
              ? `Attach evidence — ${form.label}`
              : 'Add Activity'}
        </ModalHeader>
        <ModalBody style={{ overflowX: 'hidden', padding: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              transition: 'transform 250ms ease-in-out',
              transform:
                view === 'form'
                  ? 'translateX(-33.333%)'
                  : view === 'evidence'
                    ? 'translateX(-66.666%)'
                    : 'translateX(0)',
              width: '300%',
            }}
          >
            {/* Picker */}
            <div className='tw:p-4' style={{ width: '33.333%', flexShrink: 0, boxSizing: 'border-box' }}>
              <ActivityCategoryPicker categories={categories} onSelect={openForm} />
            </div>
            {/* Form */}
            <div className='tw:p-4' style={{ width: '33.333%', flexShrink: 0, boxSizing: 'border-box' }}>
              {form.activity && (
                <ActivityFields
                  values={form.activity}
                  onUpdate={(updates) => updateForm(updates as Partial<AssetActivityEntry>)}
                  defaultDetailsOpen={false}
                  lockedFields={form.lockedFields}
                />
              )}
            </div>
            {/* Evidence */}
            <div className='tw:p-4' style={{ width: '33.333%', flexShrink: 0, boxSizing: 'border-box' }}>
              {view === 'evidence' && createdActivityId && uploadSession && initiativeId && (
                <EvidenceStep
                  initiativeId={initiativeId}
                  activityIds={[createdActivityId]}
                  uploadSession={uploadSession}
                  onContinue={() => closeModal()}
                />
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter style={{ minHeight: '6rem' }}>
          {view === 'form' && (
            <>
              <Button variant='secondary' onClick={() => { setView('picker'); setForm(EMPTY_FORM); }} disabled={isPersisting}>
                Back
              </Button>
              <Button
                className='tw:bg-emerald-700 tw:hover:bg-emerald-800 tw:text-white tw:border-0'
                onClick={handleSave}
                disabled={!canSave || isPersisting}
              >
                {isPersisting ? 'Saving...' : 'Add Activity'}
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
};
